/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, useMediaQuery, Box, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, useRef, useContext } from "react";

import { useTranslation } from 'react-i18next';
import { AssignmentInd, CallSplit, CheckCircle, Download, ThumbDown, Update, } from '@mui/icons-material';
import DialogDocumentApproverFlow from './DialogDocumentApproverFlow';
import DialogDocumentApproverInvoiceInformation from './DialogDocumentApproverInvoiceInformation';
import DialogDocumentApproverDistributions from './DialogDocumentApproverDistributions';
import FileViewer from '../../voucherSearch/components/FileViewer';
import { IApproverDocumentGuiModel, IApproverDocumentPostGuiModel, IApproverFlowGuiModel, IApproverUserGuiModel } from '../../../model/IApproverGuiModel';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';
import { toast } from 'react-toastify';
import ApproverService from '../../../service/ApproverService';
import { IFileModel } from '../../../service/model/ISourceDocumentModel';
import { UserContext } from '../../../layouts/AuthorizedLayout';
import SourceDocumentBusinessLogic from '../../../businessLogic/SourceDocumentBusinessLogic';

interface IDialogProps {
    open: boolean,
    closeDialog: (reason?: "backdropClick" | "escapeKeyDown") => void,
    organizationId: string | undefined,
    approverDocument?: IApproverDocumentGuiModel | undefined,
    approverDocuments: IApproverDocumentGuiModel[],
}

const DialogDocumentApprover = (props: IDialogProps): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const isSmAndUpScreen = useMediaQuery(theme.breakpoints.up('sm')) //Check if user has LgScreen and up

    const user = useContext(UserContext);

    const [onlyReasign, setOnlyReasign] = useState(false);

    const [loader, setLoader] = useState(false);

    const [approverDocument, setApproverDocument] = useState<IApproverDocumentGuiModel | undefined>(
        props.approverDocument ? props.approverDocument : (props.approverDocuments.length > 0 ? props.approverDocuments[0] : undefined));
    const approverDocumentsIndex = useRef(props.approverDocument ? props.approverDocuments.findIndex(e => e.id === props.approverDocument?.id) : 0);

    const [approvers, setApprovers] = useState<IApproverUserGuiModel[]>([]);
    const [currentApproverId, setCurrentApproverId] = useState<string | undefined>(undefined);
    const [flows, setFlows] = useState<IApproverFlowGuiModel[]>([]);
    const [currentFlowId, setCurrentFlowId] = useState<number | undefined>(undefined);

    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [typeActionDialog, setTypeActionDialog] = useState<"switchFlow" | "reassign" | "approveAndSendTo">("switchFlow");
    const handleOpenApproveDialog = (type: "switchFlow" | "reassign" | "approveAndSendTo") => {
        setTypeActionDialog(type);
        setOpenActionDialog(true);
    }
    const handleCloseApproveDialog = () => {
        setOpenActionDialog(false);
    }
    const handleNextDocumentInList = () => {
        if (approverDocumentsIndex.current !== -1) {
            approverDocumentsIndex.current = approverDocumentsIndex.current + 1;
            if (props.approverDocuments && (approverDocumentsIndex.current < props.approverDocuments.length)) {

                const nextDocument = props.approverDocuments[approverDocumentsIndex.current];
                setApproverDocument(nextDocument);
                setCurrentApproverId(nextDocument?.assignedUserId);

                if (nextDocument?.files && nextDocument.files.length > 0) {
                    const primaryFileIndex = nextDocument.files.findIndex(f => f.isPrimary);
                    setFileViewed(primaryFileIndex !== -1 ? nextDocument.files[primaryFileIndex] : nextDocument.files[0]);
                } else {
                    setFileViewed(null); 
                }
                getApprovers(true);
                getFlows(true);
            } else {
                props.closeDialog(); 
            }
        } else {
            props.closeDialog(); 
        }
    }

    useEffect(() => {

        let isMounted = true;

        let tempApproverDocument = props.approverDocument ? props.approverDocument : (props.approverDocuments ? props.approverDocuments[0] : undefined);

        setApproverDocument(tempApproverDocument);

        if (tempApproverDocument && tempApproverDocument.files) {
            let index = tempApproverDocument.files.findIndex(f => f.isPrimary === true);
            if (index !== -1) {
                setFileViewed(tempApproverDocument.files[index]);
            } else {
                setFileViewed(tempApproverDocument.files[0]);
            }
        }

        getApprovers(true);
        getFlows(true);

        return () => { isMounted = false };

    }, [props.approverDocument, props.approverDocuments])

    //########################################################################################
    const handleFileViewedChange = (event: SelectChangeEvent) => {
        if (approverDocument && approverDocument.files) {
            let file = approverDocument.files.find(e => e.id === parseInt(event.target.value));
            if (file)
                setFileViewed(file);
            else
                setFileViewed(null);
        }
    };
    const [fileViewed, setFileViewed] = useState<IFileModel | null>(null);

    const logicGetFile = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFile(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId;
            link.href = blobUrl;
            link.click();
        }
    }
    const logicGetFilePDF = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFileDownloadPDF(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId + ".pdf";
            link.href = blobUrl;
            link.click();
        }
    }

    //Height calculations

    const windowHeight = "100vh";
    const dialogTitleHeight = "4rem";
    const dialogContentHeight = "calc(" + windowHeight + " - " + dialogTitleHeight + " - 0.2rem)";
    const dialogContentTotalPadding = "calc( " + dialogContentHeight + " - " + theme.spacing(4) + ")";

    const handleStartApproval = async () => {
        if (props.organizationId && approverDocument && (approverDocument.assignedUserId === undefined || approverDocument.requests?.length === 0)) {
            const logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.postDocmentStartApproval(props.organizationId, approverDocument.id);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("approver.errorStartApproval" + result.errors.join(", ")))
            } else {
                getDocument(true);
            }
        }
    }

    const handleSwitchFlow = () => {
        if (validateData()) {
            handleOpenApproveDialog("switchFlow");
        }
    }
    const handleSwitchFlowButton = async () => {

        if (approverDocument && props.organizationId) {
            const service = new ApproverService();
            setLoader(true);
            const result = await service.postDocumentSwitchFlow(props.organizationId, approverDocument.id, currentFlowId);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("approver.erroSwitchFlow"))
            } else {
                handleCloseApproveDialog();
                handleNextDocumentInList();
            }
        } else {
            toast(t("approver.errorWithApprovingDocument"))
        }

    }
    const handleReassign = () => {
        if (validateData()) {
            handleOpenApproveDialog("reassign");
        }
    }
    const handleReassignButton = async () => {

        if (approverDocument && props.organizationId) {
            const service = new ApproverService();
            setLoader(true);
            const result = await service.postDocumentAssign(props.organizationId, approverDocument.id, currentApproverId);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("approver.errorAssign"))
            } else {
                handleCloseApproveDialog();
                handleNextDocumentInList();
            }
        } else {
            toast(t("approver.errorWithApprovingDocument"))
        }

    }
    const handleReject = async () => {
        if (validateData()) {
            if (approverDocument && props.organizationId) {
                const service = new ApproverService();
                setLoader(true);
                const result = await service.postDocumentReject(props.organizationId, approverDocument.id)
                setLoader(false);
                if (result.hasErrors) {
                    toast(t("approver.errorReject"))
                } else {
                    handleCloseApproveDialog();
                    handleNextDocumentInList();
                }
            } else {
                toast(t("approver.errorWithApprovingDocument"))
            }
        }
    }
    const handlePostpone = async () => {
        if (validateData()) {
            if (approverDocument && props.organizationId) {
                const service = new ApproverService();
                setLoader(true);
                const result = await service.postDocumentPostpone(props.organizationId, approverDocument.id)
                setLoader(false);
                if (result.hasErrors) {
                    toast(t("approver.errorPostpone"))
                } else {
                    handleCloseApproveDialog();
                    handleNextDocumentInList();
                }
            } else {
                toast(t("approver.errorWithApprovingDocument"))
            }
        }
    }
    const handleApprove = async () => {
        if (validateData()) {
            if (approverDocument && props.organizationId) {

                const businessLogic = new ApproverBusinessLogic();

                const data: IApproverDocumentPostGuiModel = {
                    amount: approverDocument.amount ? { value: approverDocument.amount } : undefined,
                    invoiceNo: approverDocument.invoiceNo ? { value: approverDocument.invoiceNo } : undefined,
                    voucherDate: approverDocument.voucherDate ? { value: approverDocument.voucherDate } : undefined,
                    dueDate: approverDocument.dueDate ? { value: approverDocument.dueDate } : undefined,
                    cid: approverDocument.cid ? { value: approverDocument.cid } : undefined,
                    supplierId: approverDocument.supplierId ? { value: approverDocument.supplierId } : undefined,
                    comment: approverDocument.comment ? { value: approverDocument.comment } : undefined,
                    lines: approverDocument.lines ? approverDocument.lines.map(e => {
                        return {
                            lineId: e.id,
                            debitAccountId: e.account?.accountId,
                            debitVatId: e.vatId,
                            amount: e.amount,
                            projectId: e.projectId,
                            departmentId: e.departmentId
                        }
                    }) : undefined,
                }
                setLoader(true);
                const result1 = await businessLogic.postDocument(props.organizationId, approverDocument.id, data);
                setLoader(false);

                if (result1.hasErrors) {
                    toast(t("approver.errorSaveDocument"));
                } else {
                    setLoader(true);
                    const result2 = await businessLogic.postDocmentApprove(props.organizationId, approverDocument.id)
                    setLoader(false);
                    if (result2.hasErrors) {
                        toast(t("approver.errorApprove"))
                    } else {
                        handleCloseApproveDialog();
                        handleNextDocumentInList();
                    }
                }
            } else {
                toast(t("approver.errorWithApprovingDocument"))
            }
        }
    }
    const handleApproveAndSendTo = () => {
        if (validateData()) {
            handleOpenApproveDialog("approveAndSendTo");
        }
    }
    const handleApproveAndSendToButton = async () => {

        if (approverDocument && props.organizationId) {
            const service = new ApproverService();
            setLoader(true);
            const result = await service.postDocumentApprove(props.organizationId, approverDocument.id, currentApproverId);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("approver.errorApproveAndSendTo"))
            } else {
                handleCloseApproveDialog();
                handleNextDocumentInList();
            }
        } else {
            toast(t("approver.errorWithApprovingDocument"))
        }

    }
    const getDocument = async (isMounted: boolean) => {
        if (props.organizationId && approverDocument) {
            let logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.getApproverDocument(props.organizationId, approverDocument.id);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) {
                    toast(t("approver.errorWithGettingDocument"))
                    props.closeDialog()
                    setApproverDocument(undefined);
                }
            } else {
                if (isMounted) {
                    setApproverDocument(props.approverDocument ? props.approverDocument : (props.approverDocuments ? props.approverDocuments[0] : undefined));
                    getApprovers(true);
                    getFlows(true);
                }
            }
        }
    }

    function validateData(): boolean {

        if (!approverDocument) {
            toast(t("approver.errorWithApprovingDocument"));
            return false;
        } else if (approverDocument.amount !== undefined && approverDocument.amount !== "" && !/^\d+(,\d+)?$/.test(approverDocument.amount)) {
            toast(t("approver.errorWithDocumentAmount"));
            return false;
        } else if (approverDocument.cid !== undefined && approverDocument.cid !== "" && !/^[\d]{1,24}[\d\-]$/.test(approverDocument.cid)) {
            toast(t("approver.errorWithDocumentCid"));
            return false;
        } else if (
            approverDocument.lines !== undefined &&
            approverDocument.lines.length > 0 &&
            approverDocument.lines.findIndex(e => e.amount !== undefined && e.amount !== "" && !/^\d+(,\d+)?$/.test(e.amount)) !== -1) {
            toast(t("approver.errorWithDistributionAmount"));
            return false
        } else {
            return true;
        }
    }

    function DialogActionTitle() {
        if (typeActionDialog === "switchFlow") { return <>{t("approver.switchFlow")}</> }
        else if (typeActionDialog === "reassign") { return <>{t("approver.reassign")}</> }
        else { return <>{t("approver.approveAndSendTo")}</> }
    };
    function DialogActionContent() {
        if (typeActionDialog === "switchFlow")
        {
            return <Stack direction="column" spacing={4}>
                <Box sx={{ minWidth: "11rem", backgroundColor: theme.palette.background.paper, }}>
                    <FormControl fullWidth size="small" sx={{ marginTop: theme.spacing(2) }}>
                        <InputLabel id="switch-flow">{t("approver.switchFlow")}</InputLabel>
                        <Select
                            labelId="switch-flow-label"
                            id="switch-flow"
                            value={currentFlowId ? currentFlowId.toString() : undefined}
                            label={t("approver.switchFlow")}
                            onChange={(event: SelectChangeEvent) => {
                                setCurrentFlowId(flows.find(e => e.id === parseInt(event.target.value))?.id);
                            }}
                        >
                            <MenuItem value={undefined}>{t("basic.unknown")}</MenuItem>
                            {flows.map(e => {
                                return <MenuItem value={e.id.toString()}>{e.name}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Button onClick={handleSwitchFlowButton} disabled={currentFlowId ? false : true} variant="contained" fullWidth color="success">
                    {t("approver.switchFlow")}
                </Button>
            </Stack>
        }
        else if (typeActionDialog === "reassign")
        {
            return <Stack direction="column" spacing={4}>
                <Box sx={{ minWidth: "11rem", backgroundColor: theme.palette.background.paper, }}>
                    <FormControl fullWidth size="small" sx={{marginTop: theme.spacing(2)}}>
                        <InputLabel id="reassign-user">{t("approver.reassignTo")}</InputLabel>
                        <Select
                            labelId="reassign-user-label"
                            id="reassign-user"
                            value={currentApproverId ? currentApproverId.toString() : undefined}
                            label={t("approver.reassignTo")}
                            onChange={(event: SelectChangeEvent) => {
                                setCurrentApproverId(approvers.find(e => e.user.id.toString() === event.target.value)?.user.id);
                            }}
                        >
                            <MenuItem value={undefined}>{t("basic.unknown")}</MenuItem>
                            {approvers.map(e => {
                                return <MenuItem value={e.user.id}>{e.user.fullName}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Button onClick={handleReassignButton} disabled={currentApproverId ? false : true} variant="contained" fullWidth color="success">
                    {t("approver.reassign")}
                </Button>
            </Stack>
        }
        else
        {
            return <Stack direction="column" spacing={6}>
                <Typography variant="body1">{t("approver.approveAndSendToMessage") + "."}</Typography>
                <Box sx={{ minWidth: "11rem", backgroundColor: theme.palette.background.paper, }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="select-user">{t("basic.sendTo")}</InputLabel>
                        <Select
                            labelId="select-user-label"
                            id="select-user"
                            value={currentApproverId ? currentApproverId.toString() : undefined}
                            label={t("basic.sendTo")}
                            disabled={onlyReasign}
                            onChange={(event: SelectChangeEvent) => {
                                setCurrentApproverId(approvers.find(e => e.user.id.toString() === event.target.value)?.user.id);
                            }}
                        >
                            <MenuItem value={undefined}>{t("basic.unknown")}</MenuItem>
                            {approvers.map(e => {
                                return <MenuItem value={e.user.id}>{e.user.fullName}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Button onClick={handleApproveAndSendToButton} variant="contained" fullWidth color="success">
                    {t("approver.approveAndSendTo")}
                </Button>
            </Stack>
        }
    };

    function handleUpdateComment(comment: string) {
        if (approverDocument) {
            let updatedDocument: IApproverDocumentGuiModel = {
                id: approverDocument.id,
                amount: approverDocument.amount,
                invoiceNo: approverDocument.invoiceNo,
                voucherDate: approverDocument.voucherDate,
                dueDate: approverDocument.dueDate,
                cid: approverDocument.cid,
                supplierId: approverDocument.supplierId,
                supplierName: approverDocument.supplierName,
                assignedUserId: approverDocument.assignedUserId,
                assignedUserFullName: approverDocument.assignedUserFullName,
                comment: comment,
                status: approverDocument.status,
                type: approverDocument.type,
                lines: approverDocument.lines,
                requests: approverDocument.requests,
                files: approverDocument.files
            }
            setApproverDocument(updatedDocument);
        }
    }
    //#######################################################################
    //#######################################################################
    //#######################################################################
    const getFreshDocument = async (isMounted: boolean) => {
        if (props.organizationId && props.approverDocument) {
            let businessLogic = new ApproverBusinessLogic();
            const result = await businessLogic.getApproverDocument(props.organizationId, props.approverDocument.id);
            if (result.hasErrors) {
                if (isMounted) {
                    toast(result.errors.join(", "));
                    props.closeDialog();
                }
            } else {
                if (isMounted) {
                    setApproverDocument(result.value);
                    if (user.user.id === result.value?.assignedUserId) {
                        setOnlyReasign(false);
                    } else {
                        setOnlyReasign(true);
                    }
                }
            }
        }
    }
    const getApprovers = async (isMounted: boolean) => {
        if (props.organizationId) {
            let businessLogic = new ApproverBusinessLogic();
            const result = await businessLogic.getApprovers(props.organizationId);
            if (result.hasErrors) {
                if (isMounted) toast(result.errors.join(", "));
            } else {
                if (isMounted) setApprovers(result.value);
            }
        }
    }
    const getFlows = async (isMounted: boolean) => {
        if (props.organizationId) {
            let businessLogic = new ApproverBusinessLogic();
            const result = await businessLogic.getApproverFlows(props.organizationId);
            if (result.hasErrors) {
                if (isMounted) toast(result.errors.join(", "));
            } else {
                if (isMounted) {
                    if (result.value.length > 0) {
                        setCurrentFlowId(result.value[0].id);
                    }
                    setFlows(result.value);
                }
            }
        }
    }
    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <Dialog

        fullScreen
        onClose={(event, reason) => props.closeDialog(reason)}
        open={props.open}
    >

        <DialogTitle height={dialogTitleHeight} >
            <Grid container justifyContent="space-between">
                <Grid></Grid>
                <Grid><Typography variant="button" fontSize="1rem">{t("approver.approveDocument")}</Typography></Grid>
                <Grid><Button onClick={() => props.closeDialog("escapeKeyDown")} size="medium" variant="text">{t("basic.cancel")}</Button></Grid>
            </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ height: dialogContentHeight, padding: theme.spacing(1) }}>
            <Grid container spacing={2} height="100%">
                <Grid xs={12} lg={6} flex="1 1 auto">
                    <Stack direction="column" height="100%" spacing={2}>
                        <Stack direction="row" spacing={theme.spacing(2)}>
                            <FormControl color="warning" size="small" sx={{ marginTop: theme.spacing(2), minWidth: "11rem", maxWidth: "16rem" }}>
                                <InputLabel id="select-doc">{t("search.documentChoice")}</InputLabel>
                                <Select color="warning"
                                    labelId="select-doc-label"
                                    id="select-doc"
                                    value={fileViewed?.id.toString()}
                                    label={t("search.documentChoice")}
                                    onChange={handleFileViewedChange}
                                >
                                    {approverDocument && approverDocument.files ? approverDocument.files.map(f =>
                                        <MenuItem value={f.id}>{f.fileName}</MenuItem>
                                    ) : <MenuItem value={undefined}>{t("basic.noFiles")}</MenuItem>}
                                </Select>
                            </FormControl>
                            {
                                fileViewed && fileViewed.fileName?.substring(fileViewed.fileName.length - 3) === "xml" && approverDocument && props.organizationId ?
                                    <>
                                        <Button color="warning" sx={{ width: "4rem" }}
                                            onClick={() => logicGetFile(props.organizationId ? props.organizationId : "", fileViewed.id, approverDocument.id + "")}
                                            variant="outlined" size="small">
                                            <Download fontSize="small" />
                                        </Button>
                                        <Button color="warning"
                                            onClick={() => logicGetFilePDF(props.organizationId ? props.organizationId : "", fileViewed.id, approverDocument.id + "")}
                                            startIcon={<Download />}
                                            variant="outlined" size="small" sx={{ width: "11rem", }}>
                                            {t("search.download")}
                                        </Button>
                                    </>
                                    :
                                    fileViewed && approverDocument ?
                                        <Button color="warning" sx={{ width: "4rem" }}
                                            onClick={() => logicGetFile(props.organizationId ? props.organizationId : "", fileViewed.id, approverDocument.id + "")}
                                            variant="outlined" size="small">
                                            <Download fontSize="small" />
                                        </Button>
                                        :
                                        <></>
                            }
                        </Stack>
                        <FileViewer
                            showFile={true}
                            showFileToggle={undefined}
                            sourceDocumentId={approverDocument ? approverDocument.id : undefined}
                            file={fileViewed ? fileViewed : undefined} />
                    </Stack>
                </Grid>
                <Grid xs={12} lg={6} flex="1 1 auto">
                    <Stack direction="column" spacing={2}>
                        {approverDocument && (!approverDocument.assignedUserId || approverDocument.assignedUserId === "") ? <>
                            <Grid container justifyContent="center" alignItems="center">
                                <Button
                                    disabled={onlyReasign}
                                    onClick={handleStartApproval}
                                    size="small"
                                    variant="contained"
                                    sx={{ width: '150px', backgroundColor: 'darkgreen', '&:hover': { backgroundColor: 'green' } }}>
                                    {t("approver.startApproving")}
                                </Button>
                            </Grid>
                        </> : approverDocument && approverDocument.assignedUserId && approverDocument.assignedUserId ===  user.user.id ? <>
                                <Grid container spacing={2} sx={{ height: { lg: "5.2rem" } }}>
                                    <Grid xs={6} lg={8 / 3}>
                                        <Button disabled={onlyReasign}
                                            onClick={handleSwitchFlow} size="small" fullWidth variant="contained" color="info" endIcon={<CallSplit />}>
                                            {t("approver.switchFlow")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={6} lg={4}>
                                        <Button
                                            onClick={handleReassign} size="small" fullWidth variant="contained" color="info" endIcon={<AssignmentInd />}>
                                            {t("approver.reassign")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={6} lg={8 / 3}>
                                        <Button disabled={onlyReasign}
                                            onClick={handleReject} size="small" fullWidth variant="contained" color="error" endIcon={<ThumbDown />}>
                                            {t("approver.reject")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={6} lg={8 / 3}>
                                        <Button disabled={onlyReasign}
                                            onClick={handlePostpone} size="small" fullWidth variant="contained" color="warning" endIcon={<Update />}>
                                            {t("approver.postpone")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={7} lg={7}>
                                        <Button disabled={onlyReasign}
                                            onClick={handleApprove} fullWidth variant="contained" color="success" endIcon={<CheckCircle />}>
                                            {t("approver.approve")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={5} lg={5}>
                                        <Button disabled={onlyReasign}
                                            onClick={handleApproveAndSendTo} fullWidth variant="contained" color="success" endIcon={<CheckCircle />}>
                                            {t("approver.approveAndSendTo")}
                                        </Button>
                                    </Grid>
                                </Grid>
                        </> : <></>}
                        <Grid container direction="row" sx={{ height: { lg: "29.125rem" } }}>
                            <Grid xs={12} md={5.95}>
                                <DialogDocumentApproverInvoiceInformation
                                    organizationId={props.organizationId}
                                    document={approverDocument}
                                    setDocument={setApproverDocument}
                                    onlyReasign={onlyReasign} />
                            </Grid>
                            <Grid sx={{ [theme.breakpoints.down('md')]: { height: theme.spacing(2) } }} xs={12} md={0.1}></Grid>
                            <Grid xs={12} md={5.95}>
                                <Stack direction="column" height="100%">
                                    <DialogDocumentApproverFlow
                                        isSmAndUpScreen={isSmAndUpScreen}
                                        approvalFlow={props.approverDocument && props.approverDocument.requests ? props.approverDocument.requests : []} />
                                    <Box height={theme.spacing(4.6)}></Box>
                                    <TextField disabled={onlyReasign}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        value={approverDocument ? approverDocument.comment ? approverDocument.comment : "" : ""}
                                        fullWidth
                                        multiline rows={4}
                                        onChange={(e) => handleUpdateComment(e.target.value)}
                                        id="commentId" label={t("basic.comment")} />
                                </Stack>
                            </Grid>
                        </Grid>
                        <Grid container border={1}>
                            <DialogDocumentApproverDistributions
                                isSmAndUpScreen={isSmAndUpScreen}
                                document={approverDocument}
                                setDocument={setApproverDocument}
                                heightLg={"calc(100vh - 2rem - 29.125rem - 5.2rem - " + ((approverDocument && approverDocument.assignedUserId && approverDocument.assignedUserId !== "") ? theme.spacing(14) : theme.spacing(24)) + ")"}
                                organizationId={props.organizationId}
                                onlyReasign={onlyReasign}
                            />
                        </Grid>
                    </Stack>
                </Grid>
            </Grid>
        </DialogContent>
        <Dialog
            onClose={handleCloseApproveDialog}
            open={openActionDialog}>
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid><Typography variant="button" fontSize="1rem"><DialogActionTitle /></Typography></Grid>
                    <Grid><Button onClick={() => handleCloseApproveDialog()} size="medium" variant="text">{t("basic.cancel")}</Button></Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <DialogActionContent/>
            </DialogContent>
        </Dialog>
    </Dialog>
}
export default DialogDocumentApprover;